import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { Subscription, Observable, of, defer, race } from 'rxjs';
import {
  mapTo,
  switchMap,
  take,
  tap,
  filter,
  distinctUntilChanged,
  shareReplay,
} from 'rxjs/operators';
import { AuthService } from '../../../app/auth/auth.service';
import { ReportService } from './report-service/report.service';
import {
  DownLoginReportInterface,
  SurveyTransactionStatus,
} from '../download-reports/report-response.interface';
import { ReportDownloadService } from '../../shared/services/report-service/report.service';
import { Constants } from '../../constants/trans-status';
import {
  Constants as InvoiceConstants,
  MONTH_NAME_TO_NUMBER,
  MONTH_NUMBER_TO_NAME,
} from '../invoice-operation/invoice-operation.constants';
import { ModalService } from '@purespectrum1/ui';
import { MultiBuyerCmpAuth } from '../../auth/auth.interface';
import { MultiBuyerModalComponent } from './../../create-surveys/multi-buyer-modal/multi-buyer-modal.component';

@Component({
  selector: 'ps-download-reports',
  templateUrl: './download-reports.component.html',
  styleUrls: ['./download-reports.component.css'],
})
export class DownloadReportsComponent implements OnInit, OnDestroy {
  private _subscription: Subscription = new Subscription();
  private _selectedCompanies = [];
  private _userId: number = this._auth.user!.id;
  private _userCmp: number = this._auth.user!.cmp;

  public isOperatorUser = this._auth.userType === 'operator' ? true : false;
  public isSupplierUser = this._auth.userType === 'supplier' ? true : false;
  public tabs: Tab[] = Constants.REPORTS_TAB;
  public activeTab: Tab = this.tabs[0];
  public selectMonth =
    MONTH_NUMBER_TO_NAME[
      this.convertNumberToDoubleDigit(new Date().getMonth() + 1)
    ];
  public months = InvoiceConstants.MONTHS_TO_DISPLAY;
  public selectYear = new Date().getFullYear();
  public years = InvoiceConstants.YEARS;
  public isSupplier: boolean = false;

  constructor(
    private _toastr: ToasterService,
    private _auth: AuthService,
    private _reportService: ReportService,
    public datepipe: DatePipe,
    private _reportDownloadService: ReportDownloadService,
    private _modal: ModalService
  ) {}
  strReportsArr = [];

  public daterange: any = {
    start: { _d: Date.now() },
    end: { _d: Date.now() },
  };
  public options: any = {
    locale: { format: 'YYYY-MM-DD' },
    alwaysShowCalendars: false,
  };

  public transactionStatus: SurveyTransactionStatus[] =
    [] as SurveyTransactionStatus[];

  ngOnInit(): void {
    const companyReady$ = this._ensureMultiBuyerSelected$().pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this._subscription.add(
      companyReady$
        .pipe(
          tap(() => {
            this._getTransactionStatus();
            this.getStrReports(this._userId);
            this.isSupplier = this._auth.user!.supplierAcssLvls !== 'none';
          })
        )
        .subscribe()
    );
  }

  private _ensureMultiBuyerSelected$(): Observable<
    MultiBuyerCmpAuth | undefined
  > {
    return this._auth.selectedMultiBuyerCompany$.pipe(
      switchMap((company) => {
        if (company) {
          this._userId = company.apiUserId;
          this._userCmp = company.id;
          return of(company);
        }

        const shouldOpenModal =
          this._auth.isLoggedInAsMultiBuyer() ||
          this._auth.isLoggedInAsMasterMultiBuyer();

        if (!shouldOpenModal) {
          return of(undefined);
        }

        const cmpId =
          this._auth.selectedMultiBuyerCompany?.parentCompany ??
          this._auth.user?.cmp;

        return defer(() => {
          const modalRef = this._modal.open(MultiBuyerModalComponent, {
            data: { cmpId },
          });

          return race(
            this._auth.selectedMultiBuyerCompany$.pipe(
              filter((c): c is MultiBuyerCmpAuth => !!c),
              take(1),
              tap((c) => {
                this._userId = c.apiUserId;
              })
            ),
            modalRef.onClose$.pipe(
              tap(() => {
                this._userId = this._auth.user!.id;
              }),
              mapTo(undefined)
            )
          );
        });
      }),
      distinctUntilChanged((a, b) => (a as any)?.id === (b as any)?.id)
    );
  }

  convertNumberToDoubleDigit(numberToConvert: number) {
    let number = `${numberToConvert}`;
    if (number.length == 1) {
      return `0${numberToConvert}`;
    }
    return number;
  }

  public selectedDate(value: any) {
    this.daterange.start = value.start;
    this.daterange.end = value.end;
  }

  checkActiveTab(activeIndex: any) {
    this.activeTab = this.tabs[activeIndex];
  }

  getStrReports(userId: number) {
    const strReports = this._reportService.getPsReports(userId).subscribe(
      (res: any) => {
        this.strReportsArr = res.result;
      },
      (err: { error: { msg: any } }) => {
        const errorMessage = err.error.msg;
        this._toastr.error(errorMessage);
      }
    );
    this._subscription.add(strReports);
  }
  saveReportData() {
    const payload = {
      currentTimestamp: Date.now(),
      endDt: this.datepipe.transform(this.daterange.end._d, 'yyyy-MM-dd'),
      stDt: this.datepipe.transform(this.daterange.start._d, 'yyyy-MM-dd'),
      st: this._selectedCompanies,
      u: this._userId,
      c: this._userCmp,
    };
    this._reportService.saveReportData(payload).subscribe(
      (res) => {
        this._toastr.success(res.msg);
        this.getStrReports(this._userId);
      },
      (err: { error: { msg: any } }) => {
        const errorMessage = err.error.msg;
        this._toastr.error(errorMessage);
      }
    );
  }
  selectedCmp(companies: any) {
    this._selectedCompanies = companies;
  }
  downloadLoginReport() {
    const downloadLoginReport = this._reportService
      .downloadLoginReport()
      .subscribe(
        (res: DownLoginReportInterface) => {
          window.location.assign(
            this._reportService.downloadFile(res.fileName, this._auth.token)
          );
        },
        (err: { error: { msg: string } }) => {
          const errorMessage = err.error.msg;
          this._toastr.error(errorMessage);
        }
      );
    this._subscription.add(downloadLoginReport);
  }

  /**
   * Show date range is required message if date range is not selected by the user
   */
  handleDateRangeChange() {
    // TODO
  }
  getSupplierReport(type: string) {
    const year = this.selectYear;
    const month = MONTH_NAME_TO_NUMBER[this.selectMonth];
    if (type === 'reward') {
      const companyId = null; // null because for supplier logged in users we dont send company id in request params
      this._reportDownloadService.downloadRewardCSV(companyId, month, year);
      return;
    } else {
      const companyId = this._userCmp!;
      window.location.assign(
        this._reportService.downloadStatementReport(
          companyId,
          month,
          year,
          type,
          this._auth.token
        )
      );
    }
  }

  private _getTransactionStatus() {
    const transStatus = this._reportService
      .getSurveyTransactionStatus()
      .subscribe(
        (transactionStatus: SurveyTransactionStatus[]) => {
          this.transactionStatus = transactionStatus;
        },
        (err: { error: { msg: any } }) => {
          const errorMessage = err.error.msg;
          this._toastr.error(errorMessage);
        }
      );
    this._subscription.add(transStatus);
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }
}
interface Tab {
  title: string;
  active: boolean;
  uniqueId: string;
}
